// ----------------------------------------------------------------------
// <copyright file="Izenda.ActionPopup.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * @module Action SavePopup component
 */

!(function() {
  var compose = require('recompose').compose;
  var setPropTypes = require('recompose').setPropTypes;
  var PropTypes = require('react').PropTypes;
  var withHandlers = require('recompose').withHandlers;
  var withState = require('recompose').withState;
  var lifecycle = require('recompose').lifecycle;
  var m = require('mobx');
  var UIConstants = require('logic/data/Izenda.UIConstants');
  var uiState = require('logic/Izenda.UiState');
  var appStore = require('logic/Izenda.AppStore');
  var concatArray = require('share/utils/Izenda.LazyLoadingUtils').concatArray;
  var reportsConstant = require('logic/data/Izenda.ReportsConstants');
  const { curry, flow, set, get, clone, head, filter } = require('lodash/fp');
  var categorySearchKey = null;
  var subCategorySearchKey = null;

  var savePopupActions = compose(
    withState('componentState', 'updateComponentState', () => {
      return {
        categories: [],
        isLastPageCategorySearch: false,
        subCategories: [],
        isLastPageSubCategorySearch: false,
        localGlobalCategories: []
      };
    }),
    withState('original', 'updateOriginalState', () => {
      return {
        reportName: '',
        reportType: '',
        categoryId: null,
        subCategoryId: null,
        isGlobal: false
      };
    }),

    withHandlers({
      onClosing: curry(onClosing),
      changeNameHandler: changeNameHandler,
      changeSaveInHandler: curry(m.action(changeSaveInHandler)),
      changeCategoryHandler: curry(m.action(changeCategoryHandler)),
      addNewCategoryHandler: curry(m.action(addNewCategoryHandler)),
      changeSubCategoryHandler: curry(m.action(changeSubCategoryHandler)),
      addNewSubCategoryHandler: m.action(addNewSubCategoryHandler),
      getCategories: curry(getCategories),
      getSubCategories: m.action(getSubCategories),
      searchCategoryDataHandler: curry(searchCategoryDataHandler),
      searchSubCategoryDataHandler: m.action(searchSubCategoryDataHandler),
      resetCategory: curry(resetCategory),
      resetSubCategory: m.action(resetSubCategory)
    })
  );

  function onClosing(props) {
    return function(closeCallBack, type) {
      var hidePopup = function() {
        props.onClose();
      };

      if (type === 'accept') {
        onAcceptClosing(props, hidePopup);
      } else if (type === 'cancel') {
        onCancelClosing(props, hidePopup);
      }
    };
  }

  /**
   * When click button Cancel on popup
   * @param  {Object} hidePopup function
   */
  function onCancelClosing(props, hidePopup) {
    hidePopup();
    props.validation.reset();
  }

  function onAcceptClosing(props, hidePopup) {
    var store = props.store;
    var model = props.model;

    model.saveAsMode && model.markSaveAs();
    var valManager = props.validation;

    return valManager
      .validate()
      .then(function(success) {
        if (success) {
          valManager.reset();

          if (!!props.original.isGlobal != model.isGlobal) {
            var description = model.isGlobal
              ? props.localization.getText('DASHBOARD_CONSTANTS_REPORT_MESSAGE_SELECTING_GLOBAL')
              : props.localization.getText('DASHBOARD_CONSTANTS_REPORT_MESSAGE_UNSELECTING_GLOBAL');
            return props
              .confirmFn('Confirmation', {
                description: description
              })
              .then(function() {
                return model.section
                  ? detectReportChange(props)
                  : props.save().then(function(result) {
                      result && hidePopup();
                    });
              });
          } else {
            return model.section
              ? detectReportChange(props)
              : props.save().then(function(result) {
                  result && hidePopup();
                });
          }
        }
        return success;
      })
      .then(function(saveResult) {
        return saveResult ? props.onClose(saveResult.reportId) : null;
      });
  }

  function detectReportChange(props) {
    var store = props.store;
    var section = props.section;

    return store.detectReportChange(section).then(
      function(result) {
        if (!result.success) {
          var error = result.messages[0];

          switch (error.key) {
            case Constants.DETECT_CHANGE.PHYSICAL:
              props
                .confirmFn('Information', {
                  description: error.messages[0],
                  type: 'info',
                  showCloseIndicator: true
                })
                .then(props.disableReport);
              break;
            case Constants.DETECT_CHANGE.LOGICAL:
              return props
                .confirmFn('Confirmation', {
                  description: error.messages[0]
                })
                .then(saveReport.bind(null, props));
              break;
          }
          return null;
        }

        return saveReport(props);
      }.bind(this)
    );
  }

  /**
   * Is this function being used?
   * @param {} props
   */
  function saveReport(props) {
    var store = props.store;
    var section = props.section;

    model.setIgnoreCheckChange();

    // Save as in Report Viewer
    if (!props.section && props.fieldStore) {
      var reportBody = props.fieldStore.reportBody;
      if (reportBody.hasInvisibleFields) {
        return props
          .confirmFn('Confirmation', {
            description: reportsConstant.MESSAGES.SAVE_AS_REPORT
          })
          .then(function() {
            return store.saveReport(Constants.PAGE_NAME.FIELDS);
          });
      }
    }

    // Save as in Report Designer
    return store.saveReport(section).then(function(result) {
      if (result.isDisableReport) {
        props.disableReport && props.disableReport();
      }
      return result;
    });
  }

  function changeNameHandler(props) {
    return function(name) {
      props.changeName(name);
    };
  }

  function changeSaveInHandler(props) {
    return function(value) {
      props.changeCategoryType(value);
      props.changeCategory(null, null);
      props.updateSubCategory(null, null);

      var newState = flow(set('categories', []), set('subCategories', []))(props.componentState);
      props.updateComponentState(newState);
    };
  }

  function changeCategoryHandler(props) {
    return function(key, value) {
      props.changeCategory(key, value);
      props.updateSubCategory(null, null);
      var state = set('subCategories', [], props.componentState);
      props.updateComponentState(state);
    };
  }

  function resetCategory(props) {
    return function() {
      if (categorySearchKey) {
        categorySearchKey = null;
      }
      var state = set('categories', [], props.componentState);
      props.updateComponentState(state);
    };
  }

  function getCategories(props) {
    return function(pageInfo) {
      return getCategoriesData(props, pageInfo, false);
    };
  }

  function getCategoriesData(props, pageInfo, isReset) {
    var store = props.store;
    var selectedLocalGlobalCategoryId = get(
      'id',
      getSaveInCategory(props.model.isGlobal, props.componentState.localGlobalCategories)
    );

    var request = {
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      type: 2,
      criteria: [{ key: 'name', value: categorySearchKey }],
      parentIds: [selectedLocalGlobalCategoryId],
      includeUncategory: false,
      tenantId: uiState.tenantId
    };

    return props.getCategoriesFunc(request).then(function(data) {
      var newState = clone(props.componentState);
      if (isReset) {
        newState.categories = [];
      }
      concatArray(newState.categories, data.categories, 'id', !data.isLastPage);
      newState.isLastPageCategorySearch = data.isLastPage;

      props.updateComponentState(newState);
      return data.categories;
    });
  }

  function searchCategoryDataHandler(props) {
    return function(key, pageInfo) {
      categorySearchKey = key;
      return getCategoriesData(props, pageInfo, true);
    };
  }

  function addNewCategoryHandler(props) {
    return function(category) {
      var oldState = clone(props.componentState);
      var newState = flow(set('categories', [category]), set('subCategories', []))(oldState);
      props.updateComponentState(newState);
      return true;
    };
  }

  function changeSubCategoryHandler(props) {
    return function(key, value) {
      props.updateSubCategory(key, value);
    };
  }

  function addNewSubCategoryHandler(props) {
    return function(subCategory) {
      var newState = set('subCategories', [subCategory], props.componentState);
      props.updateComponentState(newState);
      return true;
    };
  }

  function getSaveInCategory(isGlobal, localGlobalCategories) {
    return flow(filter({ isGlobal: isGlobal }), head)(localGlobalCategories);
  }

  function getSubCategories(props) {
    return function(pageInfo) {
      return getSubCategoriesData(props, pageInfo, false);
    };
  }

  function getSubCategoriesData(props, pageInfo, isReset) {
    if (String.isGuidFormat(props.model.categoryId)) {
      var selectedLocalGlobalCategoryId = get(
        'id',
        getSaveInCategory(props.model.isGlobal, props.componentState.localGlobalCategories)
      );

      var request = {
        skipItems: pageInfo.fromIndex,
        pageSize: pageInfo.pageSize,
        type: 2,
        criteria: [{ key: 'name', value: subCategorySearchKey }],
        parentIds: [selectedLocalGlobalCategoryId, props.model.categoryId],
        tenantId: uiState.tenantId
      };
      return props.getSubcategoriesFunc(request).then(function(data) {
        var newState = clone(props.componentState);
        if (isReset) {
          newState.subCategories = [];
        }
        concatArray(newState.subCategories, data.subCategories, 'id', !data.isLastPage);
        newState.isLastPageSubCategorySearch = data.isLastPage;

        props.updateComponentState(newState);
        return data.subCategories;
      });
    } else {
      return Promise.resolve([]);
    }
  }

  function searchSubCategoryDataHandler(props) {
    return function(key, pageInfo) {
      subCategorySearchKey = key;
      return getSubCategoriesData(props, pageInfo, true);
    };
  }

  function resetSubCategory(props) {
    return function() {
      if (subCategorySearchKey) {
        subCategorySearchKey = null;
        var state = set('subCategories', [], props.componentState);
        props.updateComponentState(state);
      }
    };
  }
  module.exports = savePopupActions;
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/SavePopup/Izenda.SavePopupActions.js